/**
 * A caixa "Conferência — Importar Salário (PDF)" de Outros Bancos.
 *
 * Papel: reproduz `#modal-salario-gap`, `abrirConferenciaSalarioGap` e
 * `confirmarImportacaoSalarioGap` (l. 925–953, 4710–4755): o favorecido do
 * cartão, a competência em mês e ano, o líquido editável, e "Confirmar
 * Importação", que grava o salário do cartão com a marca de importação.
 *
 * Como usar: quem monta já achou no PDF o funcionário do cartão e passa o
 * líquido e a competência lidos; a competência que o PDF não trazia começa no
 * mês aberto, com o aviso amarelo. O salário vai para a **competência
 * escolhida**, que pode não ser o mês aberto: nesse caso o cartão à vista não
 * muda, e o salário aparece quando a pessoa for ao mês dele. O aviso de sucesso
 * diz a competência e não diz o valor.
 */
import { type Favorecido, MESES_POR_EXTENSO } from '@concierge/shared';
import { useState } from 'react';

import { useNotificacao } from '@/shared/embutido';
import { lerValorDigitado, mascararCentavos } from '@/shared/lib/mascara';
import { cn } from '@/shared/lib/utils';
import { useGravarSalario } from '../hooks/usar-financas-extras';
import { AvisoDeConferencia } from './kit/aviso-de-conferencia';
import { Botao } from './kit/botao';
import { CaixaDeDialogo } from './kit/caixa-de-dialogo';
import { CLASSE_DO_CONTROLE, CLASSE_DO_SELECT, Campo, LinhaDeCampos } from './kit/campo';

interface ConferenciaDoSalarioProps {
  /** O favorecido do cartão que pediu a importação. */
  favorecido: Favorecido;
  /** O líquido do funcionário achado no PDF. */
  liquido: number;
  /** A competência lida, em `AAAA-MM`, ou `null` quando o PDF não a traz. */
  competenciaDoPdf: string | null;
  /** O mês aberto, em `AAAA-MM`, que vale quando o PDF não traz a competência. */
  mesAberto: string;
  onFechar: () => void;
}

/**
 * O texto de uma falha de gravação.
 *
 * @param erro - O que a mutação lançou.
 * @returns A mensagem da API, que nunca traz dado da família.
 */
function mensagemDe(erro: unknown): string {
  return erro instanceof Error ? erro.message : 'Não foi possível gravar.';
}

/**
 * A conferência do salário de um cartão de Outros Bancos.
 *
 * @param favorecido - O favorecido do cartão.
 * @param liquido - O líquido lido.
 * @param competenciaDoPdf - A competência lida, ou `null`.
 * @param mesAberto - O mês aberto.
 * @param onFechar - Fecha a caixa: o "✕", o véu, `Esc`, "Cancelar" e a
 *   gravação que deu certo.
 * @returns A caixa de 440 px.
 */
export function ConferenciaDoSalario({
  favorecido,
  liquido,
  competenciaDoPdf,
  mesAberto,
  onFechar,
}: ConferenciaDoSalarioProps) {
  const notificar = useNotificacao();
  const gravar = useGravarSalario();
  const inicial = competenciaDoPdf ?? mesAberto;
  const [mes, setMes] = useState(String(Number(inicial.slice(5, 7))));
  const [ano, setAno] = useState(inicial.slice(0, 4));
  const [valor, setValor] = useState(() =>
    liquido.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
  );

  /** "Confirmar Importação" (l. 4730–4755). */
  async function handleConfirmar() {
    const numeroDoMes = Number.parseInt(mes, 10);
    const numeroDoAno = Number.parseInt(ano, 10);
    const salario = lerValorDigitado(valor);
    if (!numeroDoMes || !numeroDoAno) {
      notificar('Informe a competência (mês/ano).', false);
      return;
    }
    if (!(salario > 0)) {
      notificar('Informe um valor de salário líquido válido.', false);
      return;
    }
    const mesComZero = String(numeroDoMes).padStart(2, '0');
    try {
      await gravar.mutateAsync({
        mes: {
          favorecidoId: favorecido.id,
          competencia: `${String(numeroDoAno)}-${mesComZero}`,
        },
        corpo: { salarioLiquido: salario, importado: true },
      });
      onFechar();
      notificar(
        `✓ Salário líquido importado (competência ${mesComZero}/${String(numeroDoAno)})`,
      );
    } catch (erro) {
      notificar(mensagemDe(erro), false);
    }
  }

  return (
    <CaixaDeDialogo
      titulo="Conferência — Importar Salário (PDF)"
      largura={440}
      onFechar={onFechar}
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => void handleConfirmar()} disabled={gravar.isPending}>
            Confirmar Importação
          </Botao>
        </>
      }
    >
      {competenciaDoPdf === null && (
        <AvisoDeConferencia>
          Não encontramos a competência no PDF — confira o mês/ano acima antes de confirmar.
        </AvisoDeConferencia>
      )}
      <Campo rotulo="Favorecido" id="salario-gap-favorecido">
        <div id="salario-gap-favorecido" className="py-1.5 font-semibold text-foreground">
          {favorecido.nome}
        </div>
      </Campo>
      <LinhaDeCampos colunas={2}>
        <Campo rotulo="Competência (mês)" id="salario-gap-mes">
          <select
            id="salario-gap-mes"
            value={mes}
            onChange={(evento) => setMes(evento.target.value)}
            className={CLASSE_DO_SELECT}
          >
            {MESES_POR_EXTENSO.map((nome, indice) => (
              <option key={nome} value={String(indice + 1)}>
                {nome}
              </option>
            ))}
          </select>
        </Campo>
        <Campo rotulo="Competência (ano)" id="salario-gap-ano">
          <input
            id="salario-gap-ano"
            type="number"
            value={ano}
            onChange={(evento) => setAno(evento.target.value)}
            className={CLASSE_DO_CONTROLE}
          />
        </Campo>
      </LinhaDeCampos>
      <Campo rotulo="Salário líquido (R$)" id="salario-gap-valor">
        <input
          id="salario-gap-valor"
          type="text"
          inputMode="decimal"
          value={valor}
          onChange={(evento) => setValor(mascararCentavos(evento.target.value))}
          className={cn(CLASSE_DO_CONTROLE, 'text-right')}
        />
      </Campo>
      <div className="mt-1.5 text-[11px] text-texto-fraco">
        O valor entra em Outros Bancos — só informativo, não gera lançamento nem afeta o saldo
        do C6. O PDF em si não é salvo no sistema.
      </div>
    </CaixaDeDialogo>
  );
}
